import React, { useEffect, useMemo, useState } from 'react'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'
import { ShipmentNotification } from '@/interfaces/shipmentNotification'
import Button from '@/components/misc/Button'
import { useDeleteShipmentNotification } from '../hooks/useDeleteShipmentNotification'
import { toast } from 'react-toastify'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  showEditNotification: boolean
  onClose: () => void
  notificationToEdit: ShipmentNotification | undefined
  showDeleteNotification: boolean
  onCloseDelete: () => void
  notificationToDelete: number | undefined
  onNewNotification: () => void
  showAllNotificationsMessage?: boolean
  showNoNotificationsMessage?: boolean
  onShowAllNotifications?: () => void
  onShowAllStatuses?: () => void
  onCloseNoNotificationsMessage?: () => void
}

const SHIPMENT_NOTIFICATION_TABLE_COLUMNS_STORAGE_KEY = 'shipment-notification-table-columns'

export function DataTable<TData, TValue>({
  columns,
  showEditNotification,
  onClose,
  notificationToEdit,
  showDeleteNotification,
  onCloseDelete,
  notificationToDelete,
  onNewNotification,
  showAllNotificationsMessage = false,
  showNoNotificationsMessage = false,
  onShowAllNotifications,
  onShowAllStatuses,
  onCloseNoNotificationsMessage,
}: DataTableProps<TData, TValue>) {
  // Use the table context
  const {
    currentPage,
    shipmentNotificationData,
    isLoading,
    skeletonRowCount,
    isFetching,
    isError,
    refetch,
  } = useTableContext()

  // Column visibility state with localStorage persistence
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const saved = localStorage.getItem(SHIPMENT_NOTIFICATION_TABLE_COLUMNS_STORAGE_KEY)
    return saved ? JSON.parse(saved) : {}
  })

  // Use our real delete hook
  const deleteNotificationMutation = useDeleteShipmentNotification()

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      SHIPMENT_NOTIFICATION_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnVisibility)
    )
  }, [columnVisibility])

  // Mostrar mensaje si no hay notificaciones después de una actualización
  useEffect(() => {
    if (shipmentNotificationData?.items && shipmentNotificationData.items.length === 0 && showAllNotificationsMessage) {
      // Trigger the callback to show no notifications message
      if (onCloseNoNotificationsMessage) {
        onCloseNoNotificationsMessage()
      }
    }
  }, [shipmentNotificationData?.items, showAllNotificationsMessage, onCloseNoNotificationsMessage])

  const data = useMemo(() => {
    return shipmentNotificationData?.items || []
  }, [shipmentNotificationData?.items])

  const table = useReactTable({
    data: data as TData[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  })

  const handleDeleteConfirm = () => {
    if (notificationToDelete) {
      deleteNotificationMutation.mutate(notificationToDelete, {
        onSuccess: () => {
          toast.success('Notificación eliminada exitosamente')
          onCloseDelete()
        },
        onError: (error) => {
          console.error('Error deleting notification:', error)
          toast.error('Error al eliminar la notificación')
        }
      })
    }
  }

  const handleRefreshData = () => {
    // Refetch manual de los datos
    refetch()
    toast.info('Refrescando datos del servidor...')
  }

  if (isError) {
    return (
      <div className="rounded-md border">
        <div className="flex h-24 items-center justify-center">
          <p className="text-muted-foreground">
            Error al cargar las notificaciones. Por favor, intente nuevamente.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Mensaje para mostrar todas las notificaciones */}
      {showAllNotificationsMessage && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Notificación actualizada. Por defecto solo se muestran CREATED y PENDING_TO_ARRIVE para optimizar la conexión.
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 flex gap-2">
              <button
                onClick={onShowAllNotifications}
                className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700"
              >
                Por defecto
              </button>
              <button
                onClick={onShowAllStatuses}
                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
              >
                Ver todas
              </button>
              <button
                onClick={handleRefreshData}
                className="bg-gray-600 text-white px-3 py-1 rounded-md text-sm hover:bg-gray-700"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje cuando no hay notificaciones después de actualización */}
      {showNoNotificationsMessage && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  No se encontraron notificaciones. Por defecto solo se cargan CREATED y PENDING_TO_ARRIVE. Usa filtros para ver otros estados.
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={onCloseNoNotificationsMessage}
                className="bg-yellow-600 text-white px-3 py-1 rounded-md text-sm hover:bg-yellow-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-md border bg-white flex flex-col">
        <HeaderActions 
          columns={table.getAllColumns() as any} 
          onNewNotification={onNewNotification}
        />
        <div className="overflow-auto relative">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="whitespace-nowrap">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading || isFetching ? (
                // Skeleton loading state
                Array.from({ length: skeletonRowCount }).map((_, index) => (
                  <TableRow key={index}>
                    {table.getAllColumns().map((column) => (
                      <TableCell key={column.id} className="py-3">
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                <>
                  {/* Filas con datos */}
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="hover:bg-muted/50"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              ) : (
                <>
                  {/* Mensaje de no encontrado */}
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="flex flex-col items-center justify-center gap-2">
                        <div className="text-muted-foreground">
                          No se encontraron notificaciones
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
        <Navigation />
      </div>

      {/* Alert Dialog for delete confirmation */}
      <AlertDialog open={showDeleteNotification}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar notificación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La notificación será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onCloseDelete}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
